import { LitElement, html, nothing } from 'lit';
import { property } from 'lit/decorators.js';

import '@digital-realty/ix-checkbox/ix-checkbox.js';
import '@digital-realty/ix-icon/ix-icon.js';
import '@digital-realty/ix-icon-button/ix-icon-button.js';
import '@digital-realty/ix-progress/ix-progress.js';

import type { IGenericTreeNode } from './ix-generic-tree-types.js';
import { IxGenericTreeNodeStyles } from './ix-generic-tree-styles.js';
import { IxGenericTreeTestIds } from './IxGenericTreeTestIds.js';

export class IxGenericTreeNode extends LitElement {
  static readonly styles = [IxGenericTreeNodeStyles];

  @property({ type: Object }) node!: IGenericTreeNode;

  @property({ type: Boolean }) showCheckbox: boolean = false;

  @property({ type: Boolean, reflect: true }) expanded: boolean = false;

  @property({ type: Boolean }) loading: boolean = false;

  @property({ type: Boolean }) disabled: boolean = false;

  @property({ type: Boolean }) selected: boolean = false;

  @property({ type: Boolean }) searched: boolean = false;

  @property({ type: String }) searchText: string = '';

  @property({ type: Number }) level = 0;

  @property({ type: Boolean }) filledIcons = false;

  @property({ type: Boolean }) searchEnabled = false;

  private onCheck() {
    this.node.isDirty = true;
    if (this.node.checked) {
      this.node.checked = false;
      this.node.indeterminate = false;
    } else {
      this.node.checked = true;
      this.node.indeterminate = false;
    }
    const event = new CustomEvent('check-toggle', {
      detail: {
        message: {
          node: this.node,
        },
      },
    });

    this.dispatchEvent(event);
  }

  private onNodeExpand(): void {
    if (!this.expandable) {
      return;
    }

    const event = new CustomEvent('expand-toggle', {
      detail: {
        message: {
          node: this.node,
        },
      },
    });

    this.dispatchEvent(event);
  }

  private get expandable() {
    const childrenDataIsPresent: boolean =
      this.node.children !== undefined && this.node.children.length > 0;
    const hasChildrenToLoad: boolean =
      this.node.childrenCount !== undefined && this.node.childrenCount > 0;
    return childrenDataIsPresent || hasChildrenToLoad;
  }

  private arrowIcon() {
    const icon = this.expanded ? 'arrow_drop_down' : 'arrow_right';
    return html`<ix-icon-button
      @click=${() => this.onNodeExpand()}
      style="visibility: ${this.expandable ? 'visible' : 'hidden'}"
      icon=${icon}
      test-id="tree-node-expand-toggle"
      data-testid=${IxGenericTreeTestIds.NODE_EXPAND_TOGGLE(this.node.id)}
    >
    </ix-icon-button>`;
  }

  private checkbox() {
    if (!this.showCheckbox) return nothing;

    if (this.node.inherited) {
      return html`<div class="pl-2">
        <ix-icon
          data-testid="${IxGenericTreeTestIds.CHECK_BOX_ID(this.node.id)}"
          class="inherited"
          disabled=${this.loading || this.disabled}
        >
          arrow_drop_up
        </ix-icon>
      </div>`;
    }

    return html`<div class="pl-2" style="margin-top: 0.65rem">
      <ix-checkbox
        .disabled=${this.loading || this.disabled}
        .checked=${this.node.checked}
        .indeterminate=${this.node.indeterminate}
        @click=${() => this.onCheck()}
        data-testid="${IxGenericTreeTestIds.CHECK_BOX_ID(this.node.id)}"
      ></ix-checkbox>
    </div>`;
  }

  private nodeIcon() {
    const { icon } = this.node;
    return icon
      ? html`<div class="pl-4 mt-2 node-item-icon">
          <ix-icon
            style="color: #092241; opacity: 0.6; font-size: 20px;"
            ?filled=${this.filledIcons}
          >
            ${icon}
          </ix-icon>
        </div> `
      : nothing;
  }

  private label() {
    return html`
      <p
        class="label dlr-text-small"
        data-testid="${IxGenericTreeTestIds.NODE_LABEL(this.node.id)}"
      >
        ${this.setSubstringBold(this.node.label!)}
      </p>
    `;
  }

  private childCount() {
    const count =
      this.node.childrenCount === undefined ? 0 : this.node.childrenCount;
    return html`<p
      class="pl-2 child-count dlr-text-small"
      data-testid="${IxGenericTreeTestIds.NODE_COUNT(this.node.id)}"
    >
      ${count !== 0 ? count : nothing}
    </p>`;
  }

  private secondaryLabel() {
    return this.node.secondaryLabel
      ? html`<p class="secondary-label dlr-text-caption">
          ${this.setSubstringBold(this.node.secondaryLabel!)}
        </p>`
      : nothing;
  }

  private loadingCircle() {
    return html`<div
      style="visibility: ${this.loading
        ? 'visible'
        : 'hidden'}; padding-left: 1rem;"
      class="animate-pulse"
    >
      <ix-progress .linear=${false} .indeterminate=${true}></ix-progress>
    </div>`;
  }

  private setSubstringBold(label: string) {
    if (!this.searchText || this.searchText.length < 2) return label;

    const sanitizedSearchText = this.searchText.toLowerCase().trim();
    const startIndex = label.toLowerCase().indexOf(sanitizedSearchText);

    if (startIndex === -1) {
      return label;
    }

    const endIndex = startIndex + sanitizedSearchText.length;
    return html`${label.substring(0, startIndex)}<b>${label.substring(
      startIndex,
      endIndex,
    )}</b>${label.substring(endIndex)}`;
  }

  private getBackgroundColor(): string {
    if (!this.searchEnabled || this.searchText.trim() === '') {
      return '';
    }
    if (this.selected) {
      return 'node-item-selected';
    }
    if (this.searched) {
      return 'node-item-searched';
    }
    return '';
  }

  render() {
    return html`
      <div style="padding-left: ${this.level > 0 ? '2.2rem' : '0'}">
        <div class="node-item">
          ${this.arrowIcon()} ${this.checkbox()} ${this.nodeIcon()}
          <div
            class="node-item-label ${this.getBackgroundColor()}"
            @click=${this.onNodeExpand}
            @keydown=${this.onNodeExpand}
          >
            <div class="node-item-inner">
              ${this.label()} ${this.childCount()}
            </div>
            ${this.secondaryLabel()}
          </div>
          ${this.loadingCircle()}
        </div>
        <slot></slot>
      </div>
    `;
  }
}
